import { useSyncExternalStore } from 'react';

/**
 * "Screen call" session shared by the incoming card (starts it), the global
 * ScreenCallPanel (live transcript + decisions) and GlobalVoiceProvider (auto
 * accepts the leg the server dials back when the user picks Answer). Module
 * state, because the ringing leg — and the card with it — ends the moment the
 * call is redirected into screening.
 */
export interface ScreenTarget {
  callSid: string;
  number: string;
  calledNumber?: string;
  name?: string;
}

interface State {
  active: ScreenTarget | null;
  autoAccept: { number: string; until: number } | null;
}

let state: State = { active: null, autoAccept: null };
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());

export const AUTO_ACCEPT_WINDOW_MS = 45_000;

const last10 = (n: unknown) => String(n ?? '').replace(/\D/g, '').slice(-10);

export const screenCallStore = {
  get: () => state,
  subscribe(l: () => void) { listeners.add(l); return () => { listeners.delete(l); }; },
  start(t: ScreenTarget) { state = { ...state, active: t }; emit(); },
  clear() { state = { ...state, active: null }; emit(); },
  /** The next incoming leg from this number within the window is accepted automatically. */
  armAutoAccept(number: string, now = Date.now()) { state = { ...state, autoAccept: { number, until: now + AUTO_ACCEPT_WINDOW_MS } }; emit(); },
  /** True (once) when an incoming call from `from` is the screened caller being connected. */
  consumeAutoAccept(from: string, now = Date.now()): boolean {
    const a = state.autoAccept;
    if (!a) return false;
    if (now > a.until) { state = { ...state, autoAccept: null }; return false; }
    const want = last10(a.number);
    if (want.length < 7 || want !== last10(from)) return false;
    state = { ...state, autoAccept: null };
    emit();
    return true;
  },
  reset() { state = { active: null, autoAccept: null }; emit(); },
};

export function useScreenCall(): State {
  return useSyncExternalStore(screenCallStore.subscribe, screenCallStore.get, screenCallStore.get);
}
